/**
 * Piecewise-linear time axis for the replay scrubber. Active spans render at
 * full scale; idle gaps longer than the threshold collapse to a fixed sliver,
 * so one moment days after the visit can't flatten the busy hour into 2% of
 * the track. A window with no long gaps degenerates to the plain linear axis.
 */

import type { VisitReplay } from "./replayClient";

export type Span = { start: number; end: number };

export const ms = (iso: string): number => new Date(iso).getTime();

export type TimeScale = {
  /** Merged active spans, ascending; always ≥ 1. */
  spans: Span[];
  /** Collapsed idle gaps between consecutive spans (real time bounds). */
  gaps: Span[];
  /** Total active (non-collapsed) real time, ms. */
  activeMs: number;
  /** Real time → 0..100 track position. */
  pct: (t: number) => number;
  /** Track fraction 0..1 → real time (inverse of pct, for pointer seeks). */
  timeAt: (fraction: number) => number;
  /** If `t` falls inside a collapsed gap, that gap's end — else null. */
  gapEnd: (t: number) => number | null;
  /** If `t` falls inside a collapsed gap, that gap's start — else null. */
  gapStart: (t: number) => number | null;
};

const GAP_THRESHOLD_MS = 30 * 60_000;
/** Context kept around an isolated instant so its marker isn't at a span edge. */
const POINT_PAD_MS = 2 * 60_000;
/** Each collapsed gap's width as a share of the total active time. */
const GAP_SHARE = 0.02;

export const buildTimeScale = (
  instants: number[],
  intervals: Span[],
  windowStart: number,
  windowEnd: number,
): TimeScale => {
  const candidates = [
    ...intervals,
    ...instants.map((t) => ({
      start: t - POINT_PAD_MS,
      end: t + POINT_PAD_MS,
    })),
  ]
    .map((s) => ({
      start: Math.max(s.start, windowStart),
      end: Math.min(s.end, windowEnd),
    }))
    .filter((s) => s.end >= s.start)
    .sort((a, b) => a.start - b.start);

  const spans: Span[] = [];
  for (const c of candidates) {
    const last = spans[spans.length - 1];
    if (last && c.start - last.end < GAP_THRESHOLD_MS) {
      last.end = Math.max(last.end, c.end);
    } else {
      spans.push({ ...c });
    }
  }
  if (spans.length === 0) spans.push({ start: windowStart, end: windowEnd });

  const gaps: Span[] = spans
    .slice(1)
    .map((s, i) => ({ start: spans[i].end, end: s.start }));
  const activeMs = spans.reduce((sum, s) => sum + (s.end - s.start), 0);
  const gapVirtual = Math.max(activeMs * GAP_SHARE, 60_000);
  const total = Math.max(activeMs + gaps.length * gapVirtual, 1);

  // Virtual offset at which each span starts.
  const offsets: number[] = [];
  let acc = 0;
  for (const [i, s] of spans.entries()) {
    offsets.push(acc);
    acc += s.end - s.start + (i < gaps.length ? gapVirtual : 0);
  }

  const virtualOf = (t: number): number => {
    if (t <= spans[0].start) return 0;
    for (const [i, s] of spans.entries()) {
      if (t <= s.end) return offsets[i] + Math.max(t - s.start, 0);
      const gap = gaps[i];
      if (gap && t < gap.end) {
        const fractionIntoGap = (t - gap.start) / (gap.end - gap.start);
        return offsets[i] + (s.end - s.start) + fractionIntoGap * gapVirtual;
      }
    }
    return total;
  };

  const timeAt = (fraction: number): number => {
    const v = Math.min(Math.max(fraction, 0), 1) * total;
    for (const [i, s] of spans.entries()) {
      const spanMs = s.end - s.start;
      if (v <= offsets[i] + spanMs) return s.start + (v - offsets[i]);
      const gap = gaps[i];
      if (gap && v < offsets[i] + spanMs + gapVirtual) {
        const fractionIntoGap = (v - offsets[i] - spanMs) / gapVirtual;
        return gap.start + fractionIntoGap * (gap.end - gap.start);
      }
    }
    return spans[spans.length - 1].end;
  };

  return {
    spans,
    gaps,
    activeMs,
    pct: (t) => (virtualOf(t) / total) * 100,
    timeAt,
    gapEnd: (t) => {
      for (const g of gaps) if (t > g.start && t < g.end) return g.end;
      return null;
    },
    gapStart: (t) => {
      for (const g of gaps) if (t > g.start && t < g.end) return g.start;
      return null;
    },
  };
};

/**
 * The replay window's scale. Every instant/interval feeds the axis (not just
 * visible categories, so toggling a filter never reflows the track).
 * Open-ended stays/stages contribute only their start instant — an unclosed
 * interval must not stop a trailing idle gap from collapsing.
 */
export const buildVisitScale = (replay: VisitReplay): TimeScale => {
  const start = ms(replay.windowStart);
  const end = ms(replay.windowEnd);
  const instants = [
    start,
    end,
    ...replay.moments.map((m) => ms(m.at)),
    ...replay.turns.map((t) => ms(t.at)),
    ...replay.stays
      .filter((s) => s.leftAt === null)
      .map((s) => ms(s.enteredAt)),
    ...replay.stages
      .filter((s) => s.enteredAt !== null && s.completedAt === null)
      .map((s) => ms(s.enteredAt ?? "")),
  ];
  const intervals: Span[] = [
    ...replay.capture.map((c) => ({ start: ms(c.start), end: ms(c.end) })),
    ...replay.stays
      .filter((s) => s.leftAt !== null)
      .map((s) => ({ start: ms(s.enteredAt), end: ms(s.leftAt ?? "") })),
    ...replay.stages
      .filter((s) => s.enteredAt !== null && s.completedAt !== null)
      .map((s) => ({
        start: ms(s.enteredAt ?? ""),
        end: ms(s.completedAt ?? ""),
      })),
  ];
  return buildTimeScale(instants, intervals, start, end);
};

/** "45m", "3h 20m", "2d 4h" — for the collapsed-gap break marker. */
export const formatGap = (gapMs: number): string => {
  const minutes = Math.round(gapMs / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)
    return minutes % 60 ? `${hours}h ${minutes % 60}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days}d ${hours % 24}h` : `${days}d`;
};
